import React from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend, ResponsiveContainer, Label,
} from "recharts";
import ThinScroll from "../../shared/ThinScroll";
import ThemedChartTooltip from "../../shared/ThemedChartTooltip";
import AnalysisPanel, { PanelEmpty } from "../AnalysisPanel";
import { getCiReportWeekdayWeekend } from "../../../api/competitiveIntelligence";
import { InferredOccupancy } from "../../../utils/ciFormat";
import { PanelTakeaway, fmtRate, fmtPct, SERIES_HUES } from "../shared";
import { axisProps, axisLabelStyle } from "../shared/chartTheme";

// CVD-safe (Okabe-Ito) — weekend = blue accent (the dimension we spotlight),
// weekday = neutral gray baseline. The "YOU" row in the table identifies the client.
// Hues from shared SERIES_HUES (gray is the spec's neutral gray-400).
const WEEKEND_FILL = SERIES_HUES.blue;
const WEEKDAY_FILL = SERIES_HUES.gray;

const WeekdayWeekendBody = ({ rows }) => {
  if (!rows.length) {
    return <PanelEmpty message="No weekday/weekend data for the selected category." />;
  }

  const chartData = rows.map((row) => ({
    name: row.is_client ? "YOU" : (row.property_name || "—"),
    isClient: !!row.is_client,
    weekend: row.weekend_avg ?? 0,
    weekday: row.weekday_avg ?? 0,
  }));

  return (
    <div className="p-5 space-y-5">
      <div style={{ width: "100%", height: 280 }}>
        <ResponsiveContainer>
          <BarChart data={chartData} margin={{ top: 10, right: 10, left: 10, bottom: 10 }}>
            <XAxis
              dataKey="name"
              {...axisProps}
            />
            <YAxis
              {...axisProps}
              tickFormatter={(value) => `$${value}`}
            >
              <Label
                value="Rate (USD)"
                angle={-90}
                position="insideLeft"
                offset={10}
                style={axisLabelStyle}
              />
            </YAxis>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(156,163,175,0.2)" />
            <ThemedChartTooltip
              formatter={(value) => fmtRate(value)}
              itemSorter={(item) => ({ weekday: 0, weekend: 1 }[item.dataKey] ?? 99)}
            />
            <Legend
              wrapperStyle={{ fontSize: 12 }}
              itemSorter={(item) => ({ weekday: 0, weekend: 1 }[item.dataKey] ?? 99)}
            />
            <Bar dataKey="weekday" name="Weekday" fill={WEEKDAY_FILL} radius={[2, 2, 0, 0]} />
            <Bar dataKey="weekend" name="Weekend (Fri-Sat)" fill={WEEKEND_FILL} radius={[2, 2, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <ThinScroll className="overflow-x-auto responsive-card-table-wrapper">
        <table className="w-full text-sm responsive-card-table">
          <thead className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
            <tr className="border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
              <th className="px-3 py-2.5 text-left font-semibold">Property</th>
              <th className="px-3 py-2.5 text-right font-semibold">Weekday Avg</th>
              <th className="px-3 py-2.5 text-right font-semibold">Weekend Avg</th>
              <th className="px-3 py-2.5 text-right font-semibold">Premium $</th>
              <th className="px-3 py-2.5 text-right font-semibold">Premium %</th>
              <th className="px-3 py-2.5 text-right font-semibold">Avg Occupancy</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr
                key={`${row.property_name}-${index}`}
                className={`border-b dark:border-gray-800 transition ${
                  row.is_client
                    ? "bg-[#32B1FF]/5 dark:bg-[#32B1FF]/10 hover:bg-[#32B1FF]/10"
                    : "hover:bg-gray-50 dark:hover:bg-gray-800/50"
                }`}
              >
                <td data-label="Property" className={`px-3 py-2.5 whitespace-nowrap ${row.is_client ? "font-semibold text-[#32B1FF]" : "text-gray-900 dark:text-gray-100"}`}>
                  {row.is_client ? "YOU" : (row.property_name || "—")}
                </td>
                <td data-label="Weekday Avg" className="px-3 py-2.5 text-right text-gray-700 dark:text-gray-300 whitespace-nowrap">{fmtRate(row.weekday_avg)}</td>
                <td data-label="Weekend Avg" className="px-3 py-2.5 text-right text-gray-900 dark:text-gray-100 whitespace-nowrap">{fmtRate(row.weekend_avg)}</td>
                <td data-label="Premium $" className="px-3 py-2.5 text-right text-gray-700 dark:text-gray-300 whitespace-nowrap">{fmtRate(row.weekend_premium)}</td>
                <td data-label="Premium %" className="px-3 py-2.5 text-right text-gray-700 dark:text-gray-300 whitespace-nowrap">{fmtPct(row.weekend_premium_pct)}</td>
                <td data-label="Avg Occupancy" className="px-3 py-2.5 text-right text-gray-700 dark:text-gray-300 whitespace-nowrap"><InferredOccupancy value={row.avg_occupancy} /></td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="px-4 py-2 text-[11px] text-gray-500 dark:text-gray-400">
          Weekend = Friday + Saturday check-in (DOW 5, 6).
        </div>
      </ThinScroll>
    </div>
  );
};

const WeekdayWeekendPanel = ({ clientId, categoryCode, say }) => (
  <AnalysisPanel
    id={`weekday-weekend-${clientId}-${categoryCode || "all"}`}
    title="Weekday vs Weekend Premium"
    tip="For each property: avg weekday rate vs avg weekend rate, and the % weekend premium they charge. Tells you whether competitors are charging more for Fri/Sat than you are."
    description="How much each property charges over weekday baseline on weekends. Weekend = Fri + Sat check-in."
    badge={categoryCode ? categoryCode.toUpperCase() : null}
    fetcher={() => getCiReportWeekdayWeekend(clientId, { categoryCode: categoryCode || "rv" })}
    deps={[clientId, categoryCode]}
  >
    {({ data }) => {
      const rows = Array.isArray(data) ? data : [];
      // Client first so they're easy to spot in the chart and table.
      const sorted = [...rows].sort((rowA, rowB) => (rowB.is_client ? 1 : 0) - (rowA.is_client ? 1 : 0));
      return (
        <>
          <PanelTakeaway panelKey="weekday_weekend" say={say} />
          <WeekdayWeekendBody rows={sorted} />
        </>
      );
    }}
  </AnalysisPanel>
);

export default WeekdayWeekendPanel;
